import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiSliders, FiChevronDown } from 'react-icons/fi';
import { labelFor as collectionLabelFor, isCollection as isHomepageCollection } from '../../shared/homepageCollections';
import './MidNavBar.css';

const SORT_OPTIONS = [
  { label: 'Relevance', value: 'relevance' },
  { label: 'Price: Low to High', value: 'price_asc' },
  { label: 'Price: High to Low', value: 'price_desc' },
  { label: 'Top Discounts', value: 'discount' },
  { label: 'Avg. Rating', value: 'rating' },
  { label: 'Newest First', value: 'newest' },
];

const FILTER_OPTIONS = [
  { label: '4★ & Above', value: 'rating_4' },
  { label: '3★ & Above', value: 'rating_3' },
  { label: 'Under ₹500', value: 'price_500' },
  { label: '₹500 – ₹2000', value: 'price_500_2000' },
  { label: '₹2000 – ₹5000', value: 'price_2000_5000' },
  { label: 'Above ₹5000', value: 'price_5000' },
  { label: 'Gold', value: 'color_gold' },
  { label: 'Silver', value: 'color_silver' },
  { label: 'Rose Gold', value: 'color_rose' },
  { label: 'On Sale', value: 'on_sale' },
];

// Homepage collection labels come from the shared source of truth (so every
// collection, including Gifts, gets a friendly name here). They arrive via
// ?filter= but are not choices in the Filter dropdown.

export default function MidNavBar({ initialSearch = '', initialSort = '', initialFilter = '' }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState(initialSearch);
  const [sortOpen, setSortOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeSort, setActiveSort] = useState(initialSort);
  const [activeFilter, setActiveFilter] = useState(initialFilter);
  const sortRef = useRef(null);
  const filterRef = useRef(null);

  const navigateToCatalog = (newSearch, newSort, newFilter) => {
    const params = new URLSearchParams();
    const trimmed = newSearch?.trim();
    if (trimmed) params.set('q', trimmed);
    if (newSort) params.set('sort', newSort);
    if (newFilter) params.set('filter', newFilter);
    navigate(`/catalog${params.toString() ? `?${params.toString()}` : ''}`);
  };

  useEffect(() => {
    const trimmed = search?.trim();
    if (!trimmed && !activeSort && !activeFilter) return;
    const timer = setTimeout(() => {
      navigateToCatalog(search, activeSort, activeFilter);
    }, 250);
    return () => clearTimeout(timer);
  }, [search, activeSort, activeFilter]);

  useEffect(() => {
    const h = (e) => {
      if (sortRef.current && !sortRef.current.contains(e.target)) setSortOpen(false);
      if (filterRef.current && !filterRef.current.contains(e.target)) setFilterOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const handleSort = (val) => {
    setActiveSort(prev => (prev === val ? '' : val));
    setSortOpen(false);
  };

  const handleFilter = (val) => {
    setActiveFilter(prev => (prev === val ? '' : val));
    setFilterOpen(false);
  };

  const clearSort = () => {
    setActiveSort('');
    setSortOpen(false);
  };

  const clearFilter = () => {
    setActiveFilter('');
    setFilterOpen(false);
  };

  const clearAll = () => {
    setSearch('');
    setActiveSort('');
    setActiveFilter('');
    navigate('/catalog');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    navigateToCatalog(search, activeSort, activeFilter);
  };

  return (
    <div className="mid-nav">
      {/* Sort */}
      <div className="mid-nav-sort" ref={sortRef}>
        <button className={`mid-nav-btn${activeSort ? ' active' : ''}`} onClick={() => setSortOpen(o => !o)}>
          <FiChevronDown size={14} />
          <span>{activeSort ? SORT_OPTIONS.find(s => s.value === activeSort)?.label : 'Sort By'}</span>
        </button>
        {sortOpen && (
          <div className="dropdown-menu">
            <button className={!activeSort ? 'selected' : ''} onClick={clearSort}>Clear sort</button>
            {SORT_OPTIONS.map(o => (
              <button key={o.value} className={activeSort === o.value ? 'selected' : ''} onClick={() => handleSort(o.value)}>
                {o.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Filter */}
      <div className="mid-nav-filter" ref={filterRef}>
        <button className={`mid-nav-btn${activeFilter ? ' active' : ''}`} onClick={() => setFilterOpen(o => !o)}>
          <FiSliders size={14} />
          <span>{activeFilter ? (FILTER_OPTIONS.find(f => f.value === activeFilter)?.label || (isHomepageCollection(activeFilter) ? collectionLabelFor(activeFilter) : 'Filter')) : 'Filter'}</span>
        </button>
        {filterOpen && (
          <div className="dropdown-menu filter-dropdown">
            <button className={!activeFilter ? 'selected' : ''} onClick={clearFilter}>Clear filter</button>
            {FILTER_OPTIONS.map(o => (
              <button key={o.value} className={activeFilter === o.value ? 'selected' : ''} onClick={() => handleFilter(o.value)}>
                {o.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Search */}
      <form className="mid-nav-search" onSubmit={handleSearch}>
        <input
          type="text" placeholder="Search earrings, necklaces..."
          value={search} onChange={e => setSearch(e.target.value)}
        />
        {search && <button type="button" className="clear-search-btn" onClick={() => setSearch('')}>✕</button>}
        <button type="submit" className="search-btn"><FiSearch size={16} /></button>
      </form>

      {(search || activeSort || activeFilter) && (
        <button type="button" className="clear-all-btn" onClick={clearAll}>Clear all</button>
      )}
    </div>
  );
}
